import { col, RESERVOIR_PRESSURE, type Shape } from './base.ts';

export const PHASE_RATES_STD: Shape = [
  col('oilRateStd', 'liquidRate', 'Oil production rate at standard conditions.', { suffix: 'OilRateStd' }),
  col('gasRateStd', 'gasRate', 'Gas production rate at standard conditions.', { suffix: 'GasRateStd' }),
  col('waterRateStd', 'liquidRate', 'Water production rate at standard conditions.', { suffix: 'WaterRateStd' }),
  col('liquidRateStd', 'liquidRate', 'Total liquid production rate at standard conditions.', {
    suffix: 'LiquidRateStd',
  }),
];

/**
 * The per-analysis-method estimated-rates measure family on
 * `EstimatedRates_String_Agent`: standard-conditions phase rates plus the
 * flowing bottom-hole pressure. De-prefixed per method via the method prefix
 * (`priority`, `systemAnalysis`, `chokeAnalysis`, `iprAnalysis`,
 * `vlpAnalysis`, `curveAnalysis`, `dataAnalysis`). The `Status` and `Method`
 * columns are NOT included here — only system/choke carry `Status` and only
 * priority carries `Method`, so they are layered on per view as `METHOD_STATUS`
 * / `METHOD_METHOD` to keep `prefix + suffix === source column`.
 */
export const METHOD_ESTIMATE: Shape = [
  ...PHASE_RATES_STD,
  col('fbhp', 'pressureAbsolute', 'Estimated flowing bottom-hole pressure.', { suffix: 'Fbhp' }),
];

/**
 * The per-method analysis status column. Present only on the system and choke
 * analyses (`systemAnalysisStatus`, `chokeAnalysisStatus`); layered onto those
 * two views with the method prefix.
 */
export const METHOD_STATUS: Shape = [
  col('status', 'unitless', 'Analysis status for this estimation method.', { suffix: 'Status' }),
];

/**
 * The priority-method selector column (`priorityMethod`): the analysis method
 * whose rates the priority view promotes. Present only on the priority analysis;
 * layered onto the priority view with the `priority` prefix.
 */
export const METHOD_METHOD: Shape = [
  col('method', 'unitless', 'Analysis method that produced the priority rates.', { suffix: 'Method' }),
];

/**
 * Un-prefixed estimation context shared by every `EstimatedRates_String_Agent`
 * method view: the reservoir-pressure references and the pressure source. These
 * exist as bare source columns (no method prefix), so they are kept once per
 * view with `prefix:''` (suffix === source column).
 */
export const ESTIMATED_RATES_CONTEXT: Shape = [...RESERVOIR_PRESSURE];
/**
 * Preferred-VFM-method average rates + volumes on
 * `AllocatedRates_VFM_String_Agent`. The preferred-method family is the
 * ONE clean de-prefix axis on this source: every member is `preferredVFM` +
 * suffix. The phase rates carry the `Average` segment and split associated /
 * non-associated gas, so they do NOT match `PHASE_RATES`
 * (`OilRate`/`GasRate`/`WaterRate`) — authored here as a bespoke shape applied
 * with `prefix:'preferredVFM'`.
 */
